import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  VisibilityRounded,
  VisibilityOffRounded,
  CheckCircleRounded,
  ErrorRounded,
  MarkEmailReadRounded,
} from '@mui/icons-material';
import { authTheme } from './auth-theme';
import { TeachMark } from '../../components/TeachMark';
import { useTenantStore } from '../../stores/tenant.store';
import { useUiStore } from '../../stores/ui.store';
import { tenantInitialLanguage } from '../../lib/tenant-language';
import i18n from '../../i18n';
import { usersApi } from '../../lib/api/users.api';
import { errorText } from '../../lib/error-text';
import { isAcceptableNewPassword, passwordScore } from '@dosfilos/shared';
import { activationCopy, strengthLook, type ActivationProblem } from './activation-copy';

const EMERALD = '#0f766e';
const ACCENT = '#10b981';
const INK = '#13201d';
const INK_SOFT = 'rgba(19,32,29,0.55)';

// Un botón deshabilitado se VE deshabilitado (defecto S, PM 2026-10-24): antes
// conservaba el degradado verde y el texto quedaba casi ilegible. Gris claro
// con texto gris oscuro: contraste 6,1:1 (AA), y sin parecer un botón activo.
const DISABLED_LOOK = {
  '&.Mui-disabled': {
    background: '#e5e7eb',
    color: '#4b5563',
    borderColor: '#d1d5db',
  },
} as const;

// ─── Password strength helper ─────────────────────────────────────────────────

// La regla de la contraseña vive en @dosfilos/shared: la misma que valida el
// servidor (completeInviteActivation). Aquí solo se elige cómo se ve.
function getStrength(pw: string): { score: number; label: string; color: string } {
  const score = passwordScore(pw);
  return { score, ...strengthLook(score) };
}

// ─── ActivateAccountPage ──────────────────────────────────────────────────────

type Stage = 'loading' | 'form' | 'success' | 'error';

export function ActivateAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const setCurrentTenantId = useTenantStore((s) => s.setCurrentTenantId);

  const oobCode = searchParams.get('oobCode') ?? '';
  const mode = searchParams.get('mode') ?? '';
  const inviteToken = searchParams.get('inviteToken') ?? '';
  const tenantId = searchParams.get('tenantId') ?? '';

  // resolvedOobCode is set either directly (legacy oobCode flow) or
  // after redeeming the inviteToken (7-day token flow).
  const [resolvedOobCode, setResolvedOobCode] = useState('');
  // Tenant the invite belongs to — seeded into the store on success so the user
  // lands in THIS seminary (not whichever membership sorts first).
  const [resolvedTenantId, setResolvedTenantId] = useState(tenantId);

  const [stage, setStage] = useState<Stage>('loading');
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  // Por qué no se puede activar con este enlace (etapa de error).
  const [problem, setProblem] = useState<ActivationProblem>('link-invalid');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Tenant branding (resolved from inviteToken, null until fetched) ────────
  // Starts as null so we never flash the fallback text before the real name loads.
  const [tenantBranding, setTenantBranding] = useState<{
    tenantName: string;
    primaryColor: string;
  } | null>(null);

  // ── Self-service reactivation ─────────────────────────────────────────────
  const [newEmail, setNewEmail] = useState('');
  const [requestingNew, setRequestingNew] = useState(false);
  const [newLinkSent, setNewLinkSent] = useState(false);

  const strength = getStrength(password);
  const copy = activationCopy(problem);

  // ── 1. Verify the oobCode (or redeem inviteToken) on mount ─────────────────
  useEffect(() => {
    // Invitación: el enlace se VERIFICA al abrir y se GASTA recién al crear
    // la contraseña (completeInviteActivation). Abrirlo —o que un filtro de
    // correo lo abra— ya no lo inutiliza.
    if (inviteToken) {
      usersApi
        .checkInviteToken(inviteToken)
        .then((r) => {
          setTenantBranding(r.branding);
          // La página abre en el idioma del seminario (LABTS → inglés), con la
          // MISMA regla que la app: una elección explícita del usuario gana.
          const lang = r.language
            ? tenantInitialLanguage({
                languageExplicit: useUiStore.getState().languageExplicit,
                ...r.language,
              })
            : null;
          if (lang && lang !== i18n.language) {
            useUiStore.getState().setDetectedLanguage(lang);
            void i18n.changeLanguage(lang);
          }
          if (r.status === 'valid' && r.email) {
            setResolvedEmail(r.email);
            if (r.tenantId) setResolvedTenantId(r.tenantId);
            setStage('form');
          } else {
            setProblem(r.status === 'valid' ? 'not-found' : r.status);
            setStage('error');
          }
        })
        .catch(() => {
          setTenantBranding({ tenantName: 'Seminario DosFilos', primaryColor: EMERALD });
          setProblem('not-found');
          setStage('error');
        });
      return;
    }

    // Legacy flow: direct oobCode in the URL (e.g. from password-reset email).
    if (!oobCode || mode !== 'resetPassword') {
      setProblem('link-invalid');
      setStage('error');
      return;
    }

    // Load branding from tenantId param (present when link was generated by requestPasswordReset).
    if (tenantId) {
      usersApi
        .getBrandingByTenantId(tenantId)
        .then(setTenantBranding)
        .catch(() =>
          setTenantBranding({ tenantName: 'Seminario DosFilos', primaryColor: EMERALD }),
        );
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setResolvedOobCode(oobCode);
        setResolvedEmail(email);
        setStage('form');
      })
      .catch(() => {
        setProblem('reset-unusable');
        setStage('error');
      });
  }, [oobCode, mode, inviteToken, tenantId]);

  // ── 2. Self-service: send new activation link ────────────────────────────
  async function handleRequestNewLink(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setRequestingNew(true);
    try {
      // Use the backend Cloud Function (Resend) instead of Firebase's own email service
      await usersApi.requestPasswordReset(newEmail.trim());
      setNewLinkSent(true);
    } catch {
      // Always show success to avoid email enumeration
      setNewLinkSent(true);
    } finally {
      setRequestingNew(false);
    }
  }

  // ── 3. Submit: confirm password + auto-login ──────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) return;
    if (!isAcceptableNewPassword(password)) return;

    setSaving(true);
    try {
      let email = resolvedEmail;
      if (inviteToken) {
        // Una sola operación del servidor: valida, fija la contraseña y gasta
        // el enlace. Si falla, el enlace sigue sirviendo.
        const done = await usersApi.completeInviteActivation(inviteToken, password);
        email = done.email;
        if (done.tenantId) setResolvedTenantId(done.tenantId);
      } else {
        await confirmPasswordReset(auth, resolvedOobCode, password);
      }
      await signInWithEmailAndPassword(auth, email, password);
      // Land in the invited tenant: seed its id before auth.context resolves,
      // otherwise it falls back to whichever membership sorts first.
      if (resolvedTenantId) setCurrentTenantId(resolvedTenantId);
      setStage('success');
      // Navigate to / after short delay — DefaultRedirect handles role-based routing.
      setTimeout(() => navigate('/', { replace: true }), 1500);
    } catch (err: unknown) {
      setErrorMsg(errorText(err, t('activate.errors.saveFailed')));
      setSaving(false);
    }
  }

  // ── Shared background + card wrapper (matches login / registro) ────────────
  return (
    <ThemeProvider theme={authTheme}>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          background: 'linear-gradient(135deg, #f6f4ee 0%, #eaf3ef 55%, #e2efe9 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-12rem',
            right: '-10rem',
            width: '34rem',
            height: '34rem',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${ACCENT}33, transparent 70%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Card
          sx={{
            width: 440,
            maxWidth: '100%',
            borderRadius: 4,
            border: '1px solid rgba(19,32,29,0.08)',
            boxShadow: '0 24px 60px -24px rgba(19,32,29,0.28)',
            background: '#fffdf8',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
            {/* Logo + seminary name */}
            <Stack alignItems="center" mb={3.5} spacing={1.25}>
              <TeachMark size={50} title="Teach by DosFilos" />
              {tenantBranding ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: EMERALD,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                  }}
                >
                  {tenantBranding.tenantName}
                </Typography>
              ) : (
                <Box
                  sx={{ height: 16, width: 180, bgcolor: 'rgba(19,32,29,0.06)', borderRadius: 1 }}
                />
              )}
            </Stack>

            {/* ── LOADING ── */}
            {stage === 'loading' && (
              <Stack alignItems="center" spacing={2} py={4}>
                <CircularProgress sx={{ color: EMERALD }} />
                <Typography color={INK_SOFT} variant="body2">
                  Verificando enlace de activación…
                </Typography>
              </Stack>
            )}

            {/* ── ERROR ── */}
            {stage === 'error' && (
              <Stack alignItems="center" spacing={2.5} py={2}>
                <ErrorRounded sx={{ fontSize: 48, color: '#ef4444' }} />
                <Typography variant="h6" fontWeight={800} sx={{ color: INK }} textAlign="center">
                  {t(copy.title)}
                </Typography>
                {copy.suggestLogin && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/login', { replace: true })}
                    sx={{ background: `linear-gradient(135deg, ${EMERALD} 0%, ${ACCENT} 100%)` }}
                  >
                    {t('activate.problem.signIn')}
                  </Button>
                )}

                {/* ── Self-service: pedir un enlace nuevo ── */}
                {!newLinkSent ? (
                  <>
                    <Typography variant="body2" color={INK_SOFT} textAlign="center">
                      {t(copy.hint)}
                    </Typography>
                    <Box component="form" onSubmit={handleRequestNewLink} sx={{ width: '100%' }}>
                      <Stack spacing={1.5}>
                        <TextField
                          label={t('activate.newLink.emailLabel')}
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          fullWidth
                          required
                          size="small"
                          autoComplete="email"
                        />
                        <Button
                          type="submit"
                          variant={copy.suggestLogin ? 'outlined' : 'contained'}
                          disabled={requestingNew || !newEmail.trim()}
                          startIcon={
                            requestingNew ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <MarkEmailReadRounded />
                            )
                          }
                          sx={
                            copy.suggestLogin
                              ? { ...DISABLED_LOOK }
                              : {
                                  background: `linear-gradient(135deg, ${EMERALD} 0%, ${ACCENT} 100%)`,
                                  ...DISABLED_LOOK,
                                }
                          }
                        >
                          {requestingNew
                            ? t('activate.newLink.sending')
                            : t('activate.newLink.submit')}
                        </Button>
                      </Stack>
                    </Box>
                  </>
                ) : (
                  <Alert severity="success" icon={<MarkEmailReadRounded />} sx={{ width: '100%' }}>
                    {t('activate.newLink.sent')}
                  </Alert>
                )}

                {!copy.suggestLogin && (
                  <Button
                    variant="text"
                    onClick={() => navigate('/login', { replace: true })}
                    sx={{ color: INK_SOFT, fontSize: 12 }}
                  >
                    {t('activate.problem.goToLogin')}
                  </Button>
                )}
              </Stack>
            )}

            {/* ── SUCCESS ── */}
            {stage === 'success' && (
              <Stack alignItems="center" spacing={2} py={2}>
                <CheckCircleRounded sx={{ fontSize: 56, color: '#22c55e' }} />
                <Typography variant="h6" fontWeight={800} sx={{ color: INK }}>
                  ¡Cuenta activada!
                </Typography>
                <Typography variant="body2" color={INK_SOFT} textAlign="center">
                  Iniciando sesión automáticamente…
                </Typography>
                <CircularProgress size={24} sx={{ color: EMERALD }} />
              </Stack>
            )}

            {/* ── FORM ── */}
            {stage === 'form' && (
              <>
                <Typography variant="h5" fontWeight={800} sx={{ color: INK }} mb={0.5}>
                  Activa tu cuenta
                </Typography>
                <Typography variant="body2" color={INK_SOFT} mb={3}>
                  Bienvenido/a. Crea una contraseña para{' '}
                  <Box component="span" sx={{ color: EMERALD, fontWeight: 600 }}>
                    {resolvedEmail}
                  </Box>
                </Typography>

                {errorMsg && (
                  <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
                    {errorMsg}
                  </Alert>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <Stack spacing={2.5}>
                    {/* Password */}
                    <Box>
                      <TextField
                        label="Nueva contraseña"
                        type={showPw ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        required
                        autoComplete="new-password"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPw((v) => !v)}
                                edge="end"
                                sx={{ color: 'rgba(19,32,29,0.4)' }}
                              >
                                {showPw ? <VisibilityOffRounded /> : <VisibilityRounded />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {/* Strength bar */}
                      {password.length > 0 && (
                        <Box mt={1}>
                          <LinearProgress
                            variant="determinate"
                            value={(strength.score / 5) * 100}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              bgcolor: 'rgba(19,32,29,0.08)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: strength.color,
                                borderRadius: 2,
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: strength.color, mt: 0.25, display: 'block' }}
                          >
                            {t(strength.label)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Confirm */}
                    <TextField
                      label="Confirmar contraseña"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      fullWidth
                      required
                      autoComplete="new-password"
                      error={confirm.length > 0 && password !== confirm}
                      helperText={
                        confirm.length > 0 && password !== confirm
                          ? 'Las contraseñas no coinciden'
                          : ' '
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirm((v) => !v)}
                              edge="end"
                              sx={{ color: 'rgba(19,32,29,0.4)' }}
                            >
                              {showConfirm ? <VisibilityOffRounded /> : <VisibilityRounded />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={
                        saving || password !== confirm || !isAcceptableNewPassword(password)
                      }
                      sx={{
                        background: `linear-gradient(135deg, ${EMERALD} 0%, ${ACCENT} 100%)`,
                        py: 1.5,
                        ...DISABLED_LOOK,
                      }}
                      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {saving ? t('activate.form.activating') : t('activate.form.submit')}
                    </Button>
                  </Stack>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
